import React from "react";

import { COLUMNS } from "./HeroLayout";
import { HeroLayoutSettings } from "./HeroLayout.interfaces";

/**
 * Trying to make gotchas visible. Pass `errorChecking={true} into HeroLayout
 * to avoid this.
 */
export const checkForErrors = ({
    contentAlignSecondary,
    contentWidth,
    gapWidth,
    heroDirection,
    mediaAspectRatio,
    mediaPlacement,
    mediaWidth,
    maxHeight,
    minHeight,
}: HeroLayoutSettings) => {
    if (
        heroDirection === "column" &&
        (minHeight !== undefined || maxHeight !== undefined)
    )
        return (
            <>
                <p>
                    At this time it isn’t clear what minHeight or maxHeight
                    should mean for columned layouts, so I’m just throwing the
                    error at this time. Leave it out for now.
                </p>
                <p>
                    One suggestion was to rename these to
                    minRowHeight/maxRowHeight; another idea was to only apply
                    them to media on columned layouts, but that might be
                    unintuitive.
                </p>
            </>
        );
    if (mediaPlacement === "background" && heroDirection === "column")
        return (
            <>
                <p>
                    Because mediaPlacement is `background` and content is the
                    only thing that needs positioning in the main grid,
                    heroDirections of `row` and `column` should output the same
                    content. Rather than harmonizing them I figured it was
                    simpler to just tell you to use row instead.
                </p>
                <p>
                    You can turn off errorChecking and see how it looks, and you
                    can work to make things lay out correctly in `column`
                    (without causing regressions elsewhere) if you deem it
                    necessary.
                </p>
            </>
        );
    if (
        mediaPlacement === "background" &&
        mediaAspectRatio === "stretch" &&
        !minHeight
    )
        return (
            <p>
                If the media is in the background and `mediaAspectRatio` is set
                to `stretch`, a minHeight is required.
            </p>
        );

    if (mediaPlacement !== "inline" && typeof mediaWidth !== "undefined") {
        return (
            <p>
                mediaPlacement isn’t `inline`, so mediaWidth should not be set.
            </p>
        );
    }

    if (
        contentWidth < 1 ||
        (typeof mediaWidth !== "undefined" && mediaWidth < 1)
    )
        return (
            <p>
                Error: Both contentWidth and mediaWidth should have values of at
                least 1.
            </p>
        );

    if (contentWidth > COLUMNS || (mediaWidth && mediaWidth > COLUMNS))
        return (
            <p>
                Error: contentWidth or mediaWidth is greater than the number of
                columns available.
            </p>
        );

    if (gapWidth && typeof mediaWidth === "undefined") {
        if (mediaPlacement === "inline") {
            return (
                <p>
                    If you want to set a gapWidth, then set a mediaWidth as
                    well.
                </p>
            );
        } else {
            return (
                <p>
                    Since media is not inline, there should be no need to set
                    gapWidth.
                </p>
            );
        }
    }

    if (typeof gapWidth !== "undefined" && heroDirection === "column")
        return <p>gapWidth is just for when heroDirection is a `row`.</p>;

    if (
        heroDirection === "row" &&
        contentWidth +
            (mediaWidth || COLUMNS - contentWidth) +
            (gapWidth || 0) >
            COLUMNS
    )
        return (
            <p>
                Error: contentWidth, mediaWidth, and gapWidth should not be
                greater than {COLUMNS} when heroDirection is `row``.
            </p>
        );

    if (mediaPlacement === "inline" && contentAlignSecondary === "middle")
        return (
            <p>
                Error: mediaPlacement of `inline` and contentAlignSecondary of
                `middle` are conflicting. When content is inline, you’d expect
                content to either be left/right of or above/below the media.
            </p>
        );

    if (
        mediaPlacement === "inline" &&
        heroDirection === "row" &&
        contentWidth === COLUMNS
    )
        return (
            <p>
                Error: You have content and media inline in a row but content is
                taking up all of the width.
            </p>
        );

    if (mediaPlacement === "inline" && heroDirection === "column" && mediaWidth)
        return (
            <p>
                TODO implement `mediaAlignSecondary` to enable mediaWidth
                working similarly to but independent from
                `contentAlignSecondary` in a columned context.
            </p>
        );
};
